import React, { useState } from 'react';
import {
    ActionsColumn,
    Avatar,
    Body,
    Button,
    Cell,
    Column,
    Header,
    Label,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalTitle,
    Row,
    SelectRowCell,
    Spinner,
    Table,
} from '@tlx/atlas';
import { getAvatarImageUrl } from '@tlx/astro-shared';
import { useFetchAccountingPeriodsByAnnualAccounts } from '@Page/Overview/hooks/useFetchAccountingPeriodsByAnnualAccounts.ts';
import { checkAccountingPeriods } from '@Page/Overview/actions/checkAccountingPeriods.ts';
import { FormattedMessage } from 'react-intl';
import { capitalizedMonthKeys, MonthTextKey } from '@Locale/locale.ts';
import { AccountingPeriod } from './hooks/useFetchAccountingPeriods';

export type WagePaymentsControlledProps = {
    open: boolean;
    onClose: () => void;
    year: number;
};

interface AccountingPeriodRowData {
    id: number;
    monthTextKey: MonthTextKey;
    reviewer: {
        name: string;
        imageUrl: string | undefined;
        timeOfCheck: string;
    } | null;
}

const AccountPeriodRow: React.FC<AccountingPeriodRowData> = (rowData) => {
    return (
        <Row id={String(rowData.id)}>
            <SelectRowCell disabled={rowData.reviewer !== null} />
            <Cell>
                <FormattedMessage id={rowData.monthTextKey} />
            </Cell>
            {rowData.reviewer ? (
                <>
                    <Cell className={'atl-flex atl-gap-8'}>
                        <Avatar imageUrl={rowData.reviewer.imageUrl} name={rowData.reviewer.name} size="small" />
                        <Label>{rowData.reviewer.name}</Label>
                    </Cell>
                    <Cell>
                        <Label>{rowData.reviewer.timeOfCheck}</Label>
                    </Cell>
                </>
            ) : (
                <>
                    <Cell />
                    <Cell />
                </>
            )}
        </Row>
    );
};

export function getAccountingPeriodMonthTextKey(month: number): MonthTextKey {
    // Clamp month number to valid range (0-11) | month was not 0-indexed for this company https://tripletex.slack.com/archives/C01AHR1UPFD/p1746623420849369
    const clampedMonth = Math.min(Math.max(month, 0), 11);
    return capitalizedMonthKeys[clampedMonth];
}

const mapAccountingPeriodToRowData = (accountingPeriod: AccountingPeriod): AccountingPeriodRowData => {
    const avatarImageUrl = getAvatarImageUrl(
        accountingPeriod.checkWageLogEmployeePictureId === null
            ? undefined
            : accountingPeriod.checkWageLogEmployeePictureId,
    );
    const isChecked = accountingPeriod.checkWageLogTime !== null;
    const reviewer = isChecked
        ? {
              name: accountingPeriod.checkWageLogEmployeeName || 'System',
              imageUrl: avatarImageUrl,
              timeOfCheck: accountingPeriod.checkWageLogTime as string,
          }
        : null;
    return {
        id: accountingPeriod.id,
        monthTextKey: getAccountingPeriodMonthTextKey(accountingPeriod.number),
        reviewer,
    };
};

export const WagePaymentsControlled: React.FC<WagePaymentsControlledProps> = (props) => {
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const {
        accountingPeriods,
        mutate: mutateAccountingPeriods,
        isLoading,
    } = useFetchAccountingPeriodsByAnnualAccounts(props.year, (data) => {
        const checkedPeriodIds = data?.filter((p) => p.checkWageLogTime !== null).map((p) => p.id.toString()) ?? [];
        setSelectedRows((prevState) => [...prevState, ...checkedPeriodIds]);
    });
    const [isExecuting, setIsExecuting] = useState(false);

    const checkedPeriodIds =
        accountingPeriods?.filter((p) => p.checkWageLogTime !== null).map((p) => p.id.toString()) ?? [];

    const newCheckedPeriodIds = selectedRows.filter((id) => !checkedPeriodIds.includes(id));

    const onClickCheckPeriods = async () => {
        if (selectedRows.length === 0) {
            return;
        }

        if (newCheckedPeriodIds.length <= 0) {
            return;
        }
        setIsExecuting(true);
        await checkAccountingPeriods(newCheckedPeriodIds);
        await mutateAccountingPeriods();
        setIsExecuting(false);
    };

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalLayout>
                <ModalCloseButton />
                <ModalTitle>
                    <FormattedMessage id={'text_check_wage'} /> {props.year}
                </ModalTitle>
                <ModalContent className={'tsk-w-modal'}>
                    <Table density={'compact'} selectedRows={selectedRows} onSelectedRowsChange={setSelectedRows}>
                        <Header>
                            <Row>
                                <ActionsColumn />
                                <Column>
                                    <FormattedMessage id={'text_month'} />
                                </Column>
                                <Column>
                                    <FormattedMessage id={'text_controlled_by'} />
                                </Column>
                                <Column>
                                    <FormattedMessage id={'text_date'} />
                                </Column>
                            </Row>
                        </Header>
                        <Body>
                            {isLoading && (
                                <Row>
                                    <Cell column={'1/-1'}>
                                        <Spinner />
                                    </Cell>
                                </Row>
                            )}
                            {accountingPeriods?.map(mapAccountingPeriodToRowData).map((period) => (
                                <AccountPeriodRow {...period} key={period.id} />
                            ))}
                        </Body>
                    </Table>
                </ModalContent>
                <ModalFooter className={'atl-flex atl-gap-8'}>
                    <Button
                        data-testid={'controlled-wage-payments--save'}
                        variant="primary"
                        disabled={newCheckedPeriodIds.length === 0}
                        onClick={onClickCheckPeriods}
                        loading={isExecuting}
                    >
                        <FormattedMessage id="button_save" />
                    </Button>
                    <Button
                        data-testid={'controlled-wage-payments--cancel'}
                        variant="secondary"
                        onClick={props.onClose}
                    >
                        <FormattedMessage id="button_cancel" />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
};
